import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import MetricGrid from "@/components/MetricGrid";
import Button from "@/components/Button";
import Tooltip from "@/components/Tooltip";
import { MaterialNames } from "@/components/material_symbol_names";
import type { TableAction } from "@/components/Table";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import { useNoteDeepLink } from "@/features/global/components/NotesTable/useNoteDeepLink";
import MembershipHistoryTable from "@/features/person/components/MembershipHistoryTable";
import type { MembershipHistoryRow } from "@/features/person/components/MembershipHistoryTable";
import {
  buildProgramStatusCard,
  overlayActiveProgramStatusWithOpenCcmsSuspension
} from "@/features/person/utils/utils";
import { useDuesStatus } from "@/hooks/useDuesStatus";
import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanDeleteMemberBands,
  useCanManageMemberBands,
  useCanManageSuspensions,
  useCanManageSuspensionsRestricted,
  useCanViewPersonSuspensionHistoryBool,
  useHasCapabilityBool
} from "@/hooks/useCapabilities";
import { toUTCFormat } from "@/lib/dateHelpers";
import { scrollToElement } from "@/lib/scrollHelpers";
import type { Path } from "@/router";
import { hasDuesPayingProgram } from "@/pages/people/_utils";
import type { Person } from "@/types";
import {
  NoteNotableValues as NoteNotableValueEnum,
  ProgramStatus as ProgramStatusEnum,
  ProgramTypeName
} from "@/types";
import {
  GET_MEMBERSHIP_DETAILS_QUERY,
  GET_MEMBERSHIP_DETAILS_CAREER_QUERY,
  GET_MEMBERSHIP_SUSPENSION_HISTORY_QUERY
} from "../person/queries";
import { ccmsSuspensionsForStatus } from "@/features/person/utils/ccmsSuspensions";
import ActiveYearsBreakdownModal from "./_components/ActiveYearsBreakdownModal";
import useActiveYearsBreakdown from "./_components/useActiveYearsBreakdown";
import DeleteMembershipModal from "./_components/DeleteMembershipModal";
import EditMembershipModal from "./_components/EditMembershipModal";
import RequestNonActiveClassificationModal from "./_components/RequestNonActiveClassificationModal";
import SuspensionHistoryTable from "./_components/SuspensionHistoryTable";
import UpdateMembershipModal from "./_components/UpdateMembershipModal";
import {
  eligibleNonActiveClassificationOptions,
  nonActiveClassificationRequestButtonLabel,
  requestableNonActiveClassificationCodes
} from "./_utils/nonActiveClassificationRequest";
import { usePersonMembershipAccess } from "../person/_usePersonMembershipAccess";

type PersonData = { person: Person };

export type { MembershipHistoryRow as MembershipRow } from "@/features/person/components/MembershipHistoryTable";

const EMPLOYED_REQUEST_TOOLTIP = "Member must be unemployed before a request can be made";

export default function MembershipDetails() {
  const { pgaId } = useParams();
  const { loaded, canViewPersonMembership } = usePersonMembershipAccess();
  const canViewPersonCareer = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonCareer
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [editMembershipModalOpen, setEditMembershipModalOpen] = useState(false);
  const [selectedMembershipRow, setSelectedMembershipRow] = useState<MembershipHistoryRow | null>(null);
  const [updateMembershipModalOpen, setUpdateMembershipModalOpen] = useState(false);
  const [requestNonActiveModalOpen, setRequestNonActiveModalOpen] = useState(false);
  const [deleteMembershipModalOpen, setDeleteMembershipModalOpen] = useState(false);
  const [selectedBandIdForDeletion, setSelectedBandIdForDeletion] = useState<string | null>(null);
  const [addNoteBandId, setAddNoteBandId] = useState<string | null>(null);

  const canManageMemberBands = useCanManageMemberBands();
  const canDeleteMemberBands = useCanDeleteMemberBands();
  const canManageSuspensions = useCanManageSuspensions();
  const canManageSuspensionsRestricted = useCanManageSuspensionsRestricted();
  const canViewPersonSuspensionHistory = useCanViewPersonSuspensionHistoryBool();
  const canManageAllSectionChangeRequests = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.manageAllSectionChangeRequests
  });
  const canManageOwnSectionChangeRequests = useHasCapabilityBool({
    domain: DOMAIN.SECTIONS,
    permission: PERMISSION.manageOwnSectionChangeRequests
  });
  const { id: currentUserId, sectionLegacyIds } = useCapabilities();
  const canEditMembership = useCanManageMemberBands();
  const canViewPersonBilling = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonBilling
  });
  const canViewOrders = useHasCapabilityBool({
    domain: DOMAIN.COMMERCE,
    permission: PERMISSION.viewOrders
  });

  const {
    data,
    loading,
    error,
    refetch: refetchMembershipDetails,
    startPolling: startMembershipPolling,
    stopPolling: stopMembershipPolling
  } = useQuery<PersonData>(GET_MEMBERSHIP_DETAILS_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !loaded || !canViewPersonMembership || !pgaId
  });

  const { data: careerData } = useQuery<PersonData>(GET_MEMBERSHIP_DETAILS_CAREER_QUERY, {
    variables: { pgaId },
    fetchPolicy: "network-only",
    skip: !loaded || !canViewPersonMembership || !canViewPersonCareer || !pgaId
  });

  const {
    data: suspensionHistoryData,
    loading: suspensionHistoryLoading,
    refetch: refetchSuspensionHistory,
    startPolling: startSuspensionHistoryPolling,
    stopPolling: stopSuspensionHistoryPolling
  } = useQuery<PersonData>(GET_MEMBERSHIP_SUSPENSION_HISTORY_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !loaded || !canViewPersonMembership || !canViewPersonSuspensionHistory || !pgaId
  });

  const membershipSuspensionRefetchQueries = [
    GET_MEMBERSHIP_DETAILS_QUERY,
    ...(canViewPersonSuspensionHistory ? [GET_MEMBERSHIP_SUSPENSION_HISTORY_QUERY] : [])
  ];

  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refreshMembershipAfterSuspensionMutation = useCallback(async () => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    stopMembershipPolling();
    if (canViewPersonSuspensionHistory) {
      stopSuspensionHistoryPolling();
    }

    await refetchMembershipDetails();
    if (canViewPersonSuspensionHistory) {
      await refetchSuspensionHistory();
    }

    startMembershipPolling(3000);
    if (canViewPersonSuspensionHistory) {
      startSuspensionHistoryPolling(3000);
    }
    pollingTimeoutRef.current = setTimeout(() => {
      stopMembershipPolling();
      if (canViewPersonSuspensionHistory) {
        stopSuspensionHistoryPolling();
      }
      pollingTimeoutRef.current = null;
    }, 30000);
  }, [
    canViewPersonSuspensionHistory,
    refetchMembershipDetails,
    refetchSuspensionHistory,
    startMembershipPolling,
    startSuspensionHistoryPolling,
    stopMembershipPolling,
    stopSuspensionHistoryPolling
  ]);

  const person = {
    ...(data?.person || ({} as Person)),
    ...(careerData?.person?.employments !== undefined ? { employments: careerData.person.employments } : {}),
    ...(data?.person ? { ccmsSuspensions: ccmsSuspensionsForStatus(data.person) } : {})
  };
  const { breakdownOpen, openBreakdown, closeBreakdown, activeYearsValue } = useActiveYearsBreakdown(person);
  const { duesMetric } = useDuesStatus(person, {
    skip: !canViewPersonBilling,
    skipOrders: !canViewOrders
  });
  const { bands, membershipSince, latestProgram } = person;
  const programTypeName = latestProgram?.programType?.name;

  const {
    expandRowId: highlightBandId,
    highlightNoteId,
    tableRef: membershipTableRef
  } = useNoteDeepLink({ dataReady: (bands?.length ?? 0) > 0 });

  const suspensions = suspensionHistoryData?.person?.ccmsSuspensions || [];
  const { expandRowId: highlightSuspensionId, highlightNoteId: suspensionHighlightNoteId } = useNoteDeepLink({
    dataReady: suspensions.length > 0,
    expandParam: "expandSuspensionNotes",
    highlightParam: "highlightSuspensionNote"
  });

  useEffect(() => {
    const scrollTo = searchParams.get("scrollTo");
    if (scrollTo && !loading) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("scrollTo");
      window.history.replaceState({}, "", currentUrl.toString());

      requestAnimationFrame(() => {
        scrollToElement(scrollTo);
      });
    }
  }, [searchParams, loading]);

  useEffect(() => {
    return () => {
      stopMembershipPolling();
      if (canViewPersonSuspensionHistory) {
        stopSuspensionHistoryPolling();
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [canViewPersonSuspensionHistory, stopMembershipPolling, stopSuspensionHistoryPolling]);

  if (!loaded || !canViewPersonMembership) {
    return null;
  }

  if (error) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Membership Details"
        message={error.message}
        className="mt-12"
      />
    );
  }

  const primaryEmployment = person.employments?.find((e) => e.isPrimary);
  const membershipClassification = latestProgram?.band?.classification?.name;
  const jobClassification = primaryEmployment?.jobClassification;

  const programSinceFormatted = toUTCFormat(membershipSince || "");
  const isMemberAssociateOrAffiliate =
    programTypeName === ProgramTypeName.Member ||
    programTypeName === ProgramTypeName.Associate ||
    programTypeName === ProgramTypeName.Affiliate;

  const showActiveYearsOnly = programTypeName === ProgramTypeName.Member && activeYearsValue != null;

  const membershipStatus = overlayActiveProgramStatusWithOpenCcmsSuspension(
    latestProgram?.status,
    person.ccmsSuspensions
  );
  const metrics: MetricCardType<Path>[] = isMemberAssociateOrAffiliate
    ? [
        ...(showActiveYearsOnly
          ? []
          : [
              {
                label: `${programTypeName} Since`,
                value: programSinceFormatted,
                iconName: "badge" as const
              }
            ]),
        ...(programTypeName === ProgramTypeName.Member && activeYearsValue != null
          ? [
              {
                label: programSinceFormatted
                  ? `Active Years of Membership\nElection Date: ${programSinceFormatted}`
                  : "Active Years of Membership",
                value: activeYearsValue,
                iconName: "schedule" as const,
                onClick: openBreakdown,
                tooltip: showActiveYearsOnly
                  ? {
                      ...(programSinceFormatted && { title: `Member since ${programSinceFormatted}` }),
                      supportingText: "View how each membership band contributes to this total."
                    }
                  : { supportingText: "View how each membership band contributes to this total." }
              }
            ]
          : []),
        {
          label: "Classification",
          value: membershipClassification,
          iconName: "badge",
          ...(pgaId && {
            onClick: () => navigate(`/people/${pgaId}/career/employment` as any)
          }),
          ...(jobClassification &&
            membershipClassification &&
            jobClassification !== membershipClassification && {
              tooltip: { supportingText: `Employment Classification: ${jobClassification}` }
            })
        },
        {
          ...buildProgramStatusCard(
            latestProgram?.status,
            person.ccmsSuspensions,
            latestProgram?.band?.terminatedReason,
            latestProgram?.band?.terminatesOn,
            "Membership Status"
          ),
          iconName: "badge",
          ...(membershipStatus === ProgramStatusEnum.SUSPENDED &&
            canViewPersonSuspensionHistory && {
              scrollToId: "suspension-history"
            })
        } as MetricCardType<Path>,
        ...(hasDuesPayingProgram(person) && canViewPersonBilling && duesMetric ? [duesMetric] : [])
      ]
    : [];

  const personSectionLegacyId = person.primarySection?.legacyId || person.sectionByHomeAddress?.legacyId || null;
  const canRequestNonActiveForSection =
    canManageAllSectionChangeRequests ||
    (canManageOwnSectionChangeRequests && !!personSectionLegacyId && sectionLegacyIds.includes(personSectionLegacyId));
  const requestableCodes = requestableNonActiveClassificationCodes(person.lifeMemberEligibility);
  const eligibleRequestOptions = eligibleNonActiveClassificationOptions(person.lifeMemberEligibility);
  const showRequestNonActiveButton =
    !canManageMemberBands &&
    canRequestNonActiveForSection &&
    programTypeName === ProgramTypeName.Member &&
    requestableCodes.length > 0;
  const requestButtonDisabled = person.lifeMemberEligibility?.hasOpenEmployment === true;
  const requestButtonLabel = nonActiveClassificationRequestButtonLabel(requestableCodes);

  const headingActions: TableAction[] = [];
  if (canManageMemberBands && programTypeName === ProgramTypeName.Member) {
    headingActions.push({
      label: "Update Membership",
      icon: "add" as MaterialNames,
      variant: "outlined" as const,
      onClick: () => setUpdateMembershipModalOpen(true),
      testId: "update-membership-button"
    });
  } else if (showRequestNonActiveButton) {
    headingActions.push({
      label: requestButtonLabel,
      icon: "add" as MaterialNames,
      variant: "outlined" as const,
      onClick: () => setRequestNonActiveModalOpen(true),
      disabled: requestButtonDisabled,
      testId: "request-non-active-classification-button",
      ...(requestButtonDisabled
        ? {
            render: () => (
              <Tooltip supportingText={EMPLOYED_REQUEST_TOOLTIP} variant="plain" color="black">
                <span className="inline-flex">
                  <Button variant="outlined" icon="add" disabled testId="request-non-active-classification-button">
                    {requestButtonLabel}
                  </Button>
                </span>
              </Tooltip>
            )
          }
        : {})
    });
  }

  return (
    <>
      {!!metrics.length && <MetricGrid metrics={metrics} layout="expanded" />}
      <MembershipHistoryTable
        showHeading={false}
        headingTopSpacing="none"
        bands={bands ?? []}
        loading={loading}
        pgaId={pgaId || ""}
        primaryBandId={latestProgram?.band?.id}
        refetchQueries={[GET_MEMBERSHIP_DETAILS_QUERY]}
        highlightBandId={highlightBandId}
        highlightNoteId={highlightNoteId}
        tableRef={membershipTableRef}
        headingActions={headingActions}
        actions={{
          canAddNote: canManageMemberBands,
          canEditMembership,
          canDeleteMembership: canDeleteMemberBands,
          onAddNote: setAddNoteBandId,
          onEditMembership: (row) => {
            setSelectedMembershipRow(row);
            setEditMembershipModalOpen(true);
          },
          onDeleteMembership: (bandId) => {
            setSelectedBandIdForDeletion(bandId);
            setDeleteMembershipModalOpen(true);
          }
        }}
      />

      {canViewPersonSuspensionHistory ? (
        <SuspensionHistoryTable
          suspensions={suspensions}
          loading={loading || suspensionHistoryLoading}
          canEdit={canManageMemberBands}
          canAddSuspension={canManageSuspensions}
          canManageSuspensions={canManageSuspensions}
          canManageSuspensionsRestricted={canManageSuspensionsRestricted}
          pgaId={pgaId}
          personId={person.id}
          refetchQueries={membershipSuspensionRefetchQueries}
          isPersonActive={latestProgram?.status === ProgramStatusEnum.ACTIVE}
          currentUserId={currentUserId || undefined}
          autoExpandSuspensionId={highlightSuspensionId}
          highlightNoteId={suspensionHighlightNoteId}
          onSuspensionMutationSuccess={refreshMembershipAfterSuspensionMutation}
        />
      ) : null}
      {editMembershipModalOpen && (
        <EditMembershipModal
          open={editMembershipModalOpen}
          onClose={() => {
            setEditMembershipModalOpen(false);
            setSelectedMembershipRow(null);
          }}
          membershipRow={selectedMembershipRow}
          personId={person.id}
          pgaId={pgaId || ""}
        />
      )}

      {updateMembershipModalOpen && person.id && pgaId && (
        <UpdateMembershipModal
          open={updateMembershipModalOpen}
          onClose={() => setUpdateMembershipModalOpen(false)}
          person={person}
          pgaId={pgaId}
        />
      )}

      {requestNonActiveModalOpen && person.id && (
        <RequestNonActiveClassificationModal
          open={requestNonActiveModalOpen}
          onClose={() => setRequestNonActiveModalOpen(false)}
          personId={person.id}
          options={eligibleRequestOptions}
        />
      )}

      {deleteMembershipModalOpen && selectedBandIdForDeletion && pgaId && (
        <DeleteMembershipModal
          open={deleteMembershipModalOpen}
          onClose={() => {
            setDeleteMembershipModalOpen(false);
            setSelectedBandIdForDeletion(null);
          }}
          bandId={selectedBandIdForDeletion}
          pgaId={pgaId}
        />
      )}

      {breakdownOpen && pgaId && (
        <ActiveYearsBreakdownModal
          open={breakdownOpen}
          onClose={closeBreakdown}
          pgaId={pgaId}
          activeYearsValue={activeYearsValue}
        />
      )}

      {addNoteBandId && (
        <AddEditNoteSideSheet
          open={!!addNoteBandId}
          onClose={() => setAddNoteBandId(null)}
          notableId={addNoteBandId}
          notableType={NoteNotableValueEnum.MEMBERSHIP_BAND}
          refetchQueries={[GET_MEMBERSHIP_DETAILS_QUERY]}
          currentUserId={currentUserId || undefined}
          pgaId={pgaId}
          objectType="person"
        />
      )}
    </>
  );
}
